import { useState } from "react";

import { useParams, useNavigate, Link, useLocation } from "react-router-dom";
import { Container } from "@/components/Container";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import ScheduledJobs from "@/components/ScheduledJobs";
import AutomationConfig from "@/components/AutomationConfig";
import InteractionLog from "@/components/automate/InteractionLog";
import { Label } from "@/components/ui/label";
import { Bot, Calendar, MessageSquare, LoaderCircle } from "lucide-react";
import { Check, ChevronsUpDown } from "lucide-react";
import { cn } from "@/lib/utils";
import CreditWarningModal from "@/components/CreditWarningModal";

import { usePersonasQueryNoPublic } from "@/api/personas";
import { usePrivy } from "@privy-io/react-auth";
import { useSelectedPersona } from "@/hooks/use-selected-persona";
import { useTabUrlState } from "@/hooks/use-tab-url-state";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";

const Tab = {
  agents: "agents",
  executionPlan: "execution-plan",
  interactionLog: "interaction-log",
} as const;

type Tab = (typeof Tab)[keyof typeof Tab];
const TABS = Object.values(Tab);

const EmptyStateModal = ({ tab }: { tab: Tab }) => {
  const navigate = useNavigate();

  const modalContent = {
    agents: {
      title: "Create an Agent to Automate Twitter Engagement",
      description:
        "To create an automated posting schedule for your AI agent on twitter, you'll need to create an agent first",
      imageSrc: "/guidance-images/automation.png",
      nextRoute: "/agents/new",
      closeRoute: "/autonomous#execution-plan",
    },
    "execution-plan": {
      title: "Create an Agent for Scheduled Tasks",
      description:
        "To review your automated schedule or manual schedule X content, you'll need to create an agent first",
      imageSrc: "/guidance-images/schedule.png",
      nextRoute: "/agents/new",
      closeRoute: "/autonomous#interaction-log",
    },
    "interaction-log": {
      title: "Create an Agent to Track its AI Interactions",
      description:
        "To start tracking agent interactions, you'll need to create an agent first",
      imageSrc: "/guidance-images/interactions.png",
      nextRoute: "/agents/new",
      closeRoute: "/agents",
    },
  };

  const content = modalContent[tab];

  const handleClose = () => {
    navigate(content.closeRoute, { replace: true });
  };

  return (
    <Dialog open={true} onOpenChange={handleClose}>
      <DialogContent className="bg-black/100 border-white/10 border-border md:max-w-[60%] w-[40vw] h-[75vh]">
        <DialogHeader>
          <DialogTitle className="text-primary text-xl">
            {content.title}
          </DialogTitle>
          <DialogDescription className="text-white/ 60 text-lg">
            {content.description}
          </DialogDescription>
        </DialogHeader>
        <div className="flex-1 flex items-center justify-center h[20%] border border-white/10 rounded-lg bg-black/40">
          <img
            src={content.imageSrc}
            alt="Guidance"
            className="h-[45vh] object-contain"
          />
        </div>
        <div className="flex justify-center">
          <Button
            className="bg-[#DFFE00] hover:bg-[#DFFE00]/90 text-black font-medium px-8 py-3"
            onClick={() => navigate(content.nextRoute, { replace: true })}
          >
            Create Agent
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default function AutonomousAgents() {
  const { search } = useLocation();
  const [selectedPersonaId, setSelectedPersonaId] = useSelectedPersona();
  const { personaId: routePersonaId } = useParams();
  let actualPersonaId = selectedPersonaId || routePersonaId || "";
  const [personaOpen, setPersonaOpen] = useState(false);

  const { authenticated } = usePrivy();
  const { data: personas = [], isLoading: personasLoading } =
    usePersonasQueryNoPublic(authenticated);

  const [tab] = useTabUrlState(TABS);

  const handlePersonaChange = (value: string) => {
    setPersonaOpen(false);
    setSelectedPersonaId(value);
  };

  const renderContent = () => {
    if (personasLoading) {
      return (
        <div className="flex items-center justify-center h-screen">
          <LoaderCircle className="h-12 w-12 animate-spin text-primary" />
        </div>
      );
    }

    // Derive the specific persona data from the personas array
    const personaData = personas.find((p) => p.persona_id === actualPersonaId);
    if (!personaData) {
      actualPersonaId = "";
    }
    const hasTwitterAuth = !!personaData?.twitter;

    if (personas.length === 0) {
      return <EmptyStateModal tab={tab as Tab} />;
    }

    return (
      <>
        {tab === Tab.agents && (
          <div className="flex flex-col gap-4 justify-center w-full  mx-auto">
            {actualPersonaId ? (
              <AutomationConfig
                selectedPersonaId={actualPersonaId}
                hasTwitterAuth={hasTwitterAuth}
              />
            ) : (
              <div className="text-center py-12 text-muted-foreground">
                <Bot className="w-12 h-12 mx-auto mb-4 opacity-50" />
                <h3 className="text-lg font-semibold mb-2">
                  No Agent Selected
                </h3>
                <p>
                  Select an agent above to configure their autonomous behavior.
                </p>
              </div>
            )}
          </div>
        )}

        {tab === Tab.executionPlan && actualPersonaId ? (
          <div className="flex flex-col gap-4 justify-center w-full mx-auto mb-16">
            <ScheduledJobs selectedPersonaId={actualPersonaId} />
          </div>
        ) : tab === Tab.executionPlan ? (
          <div className="text-center py-12 text-muted-foreground">
            Please select an agent to view and manage their execution plan.
            <p className="mt-2 text-sm">
              You can configure automated tasks and schedules for your agent.
            </p>
          </div>
        ) : null}

        {tab === Tab.interactionLog && actualPersonaId && (
          <div className="flex flex-col gap-4 justify-center max-w-full mx-auto mb-16">
            <h2 className="text-xl font-semibold">Interaction Log</h2>
            <p className="text-sm text-muted-foreground mb-4">
              Below is a history of this agent's interactions and logs.
            </p>
            <InteractionLog agentId={actualPersonaId} />
          </div>
        )}
        {tab === Tab.interactionLog && !actualPersonaId && (
          <div className="text-center py-12 text-muted-foreground">
            <h3 className="text-lg font-semibold mb-2">No Agent Selected</h3>
            <p>Please select an agent to view their interaction log.</p>
          </div>
        )}
      </>
    );
  };

  return (
    <Container>
      {actualPersonaId && (
        <CreditWarningModal
          agentId={actualPersonaId}
          minimumCredits={10}
          showDebugInfo={import.meta.env.DEV}
        />
      )}
      <div className="flex flex-col justify-center items-center py-3">
        <div className="flex flex-row items-center gap-3 w-full max-w-xl px-4 ml-20">
          <Label htmlFor="persona-selector">Agent</Label>
          <Popover open={personaOpen} onOpenChange={setPersonaOpen}>
            <PopoverTrigger asChild>
              <Button
                id="persona-selector"
                data-testid="persona-selector"
                variant="outline"
                role="combobox"
                aria-expanded={personaOpen}
                className="w-full max-w-64"
              >
                <span className="w-full truncate">
                  {selectedPersonaId
                    ? personas.find(
                        (persona) => persona.persona_id === selectedPersonaId,
                      )?.name || "Select agent..."
                    : "Select agent..."}
                </span>
                <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
              </Button>
            </PopoverTrigger>
            <PopoverContent className="p-0">
              <Command>
                <CommandInput placeholder="Search agent..." />
                <CommandList>
                  <CommandEmpty>No agent found.</CommandEmpty>
                  <CommandGroup>
                    {personas
                      .sort((a, b) => a.name.localeCompare(b.name))
                      .map((persona) => (
                        <CommandItem
                          key={persona.persona_id}
                          value={persona.persona_id}
                          onSelect={handlePersonaChange}
                          keywords={[persona.name]}
                        >
                          <Check
                            className={cn(
                              "mr-2 h-4 w-4",
                              selectedPersonaId === persona.persona_id
                                ? "opacity-100"
                                : "opacity-0",
                            )}
                          />
                          {persona.name}
                        </CommandItem>
                      ))}
                  </CommandGroup>
                </CommandList>
              </Command>
            </PopoverContent>
          </Popover>
        </div>
        <div className="flex justify-center gap-4 mt-8 mb-8">
          <Link to={{ hash: Tab.agents, search }}>
            <Button
              variant={tab === Tab.agents ? "default" : "outline"}
              className="min-w-[180px]"
            >
              <Bot className="w-4 h-4 mr-2" />
              Autonomous Agents
            </Button>
          </Link>
          <Link to={{ hash: Tab.executionPlan, search }}>
            <Button
              variant={tab === Tab.executionPlan ? "default" : "outline"}
              className="min-w-[180px]"
            >
              <Calendar className="w-4 h-4 mr-2" />
              Agent Execution Plan
            </Button>
          </Link>
          <Link to={{ hash: Tab.interactionLog, search }}>
            <Button
              variant={tab === Tab.interactionLog ? "default" : "outline"}
              className="min-w-[180px]"
            >
              <MessageSquare className="w-4 h-4 mr-2" />
              Interaction Log
            </Button>
          </Link>
        </div>
        {renderContent()}
      </div>
    </Container>
  );
}
